import React, { useEffect, lazy, Suspense } from 'react';
import { syncServerPrefs } from './utils/serverPrefs';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import toast, { Toaster, ToastBar } from 'react-hot-toast';
import { useAppStore } from './store/useStore';
import { canSeeXStocks } from './utils/xstocksAccess';
import { canSeeOptions } from './utils/optionsAccess';
import { isAdmin } from './utils/adminAccess';
import { Login } from './pages/Login';
import { wsManager } from './api/websocket';
import { AppShell } from './layout/AppShell';
import { PageErrorBoundary } from './components/PageErrorBoundary';
import { LoadingState } from './ui/State';
import { withPageNs } from './i18n';

/**
 * Все страницы грузятся по требованию — и терминал тоже.
 *
 * Раньше все 20 страниц лежали в одном бандле на 977 кБ, и человек, открывший
 * чат или список ключей, всё равно скачивал lightweight-charts, ChartPanel и
 * OrderPanel — самые тяжёлые куски приложения — до того, как на экране
 * появлялось хоть что-то. Потом страницы разнесли, но терминал оставили
 * статическим как стартовый маршрут — и те же графики с формой ордера
 * по-прежнему качал каждый, кто открывал сканер, алерты или страницу входа
 * (главный файл вырос до 1.25 МБ).
 *
 * Теперь терминал — такой же отдельный чанк. Чтобы на «/» не появилась лишняя
 * ступенька «скачали приложение → только потом начали качать терминал»:
 *   - index.html предзагружает чанк терминала сразу, если открыт «/»
 *     (см. плагин preloadHints в vite.config.ts);
 *   - здесь импорт стартует при загрузке модуля, не дожидаясь первого рендера.
 * Заглушка Suspense в Protected на «/» мелькает только пока чанк в пути.
 */
const loadTerminal = () => import('./pages/Terminal');
const Terminal          = lazy(loadTerminal);
// Стартовый маршрут: начинаем качать терминал сразу. Ошибку глотаем — её
// покажет lazy при рендере (PageErrorBoundary), а не необработанный промис.
if (typeof window !== 'undefined' && window.location.pathname === '/') {
  void loadTerminal().catch(() => { /* см. выше */ });
}
const DashboardV3       = lazy(() => import('./pages/DashboardV3'));
const ProfilePage       = lazy(() => import('./pages/SettingsV3'));
const SecurityPage      = lazy(() => import('./pages/Security'));
const AchievementsPage  = lazy(() => import('./pages/Achievements'));
const FeedHealthPage    = lazy(() => import('./pages/FeedHealth'));
const GamesPage         = lazy(() => import('./pages/GamesPage'));
const ChatPage          = lazy(() => import('./pages/ChatPage'));
const RouteDetail       = lazy(() => import('./pages/RouteDetail'));
const ApiKeysPage       = lazy(withPageNs('apiKeys', () => import('./pages/ApiKeys')));
const BalancesPage      = lazy(() => import('./pages/Balances'));
const AlertsPage        = lazy(() => import('./pages/Alerts'));
const FundingPage       = lazy(() => import('./pages/Funding'));
const XStocksPage       = lazy(() => import('./pages/XStocks'));
// Раздел xStocks — только для своих (utils/xstocksAccess); остальным — на сканер.
const XStocksGate: React.FC = () => {
  const user = useAppStore(s => s.user);
  if (!user) return null;
  return canSeeXStocks(user) ? <XStocksPage /> : <Navigate to="/scanner" replace />;
};
const OptionsPage       = lazy(() => import('./pages/Options'));
// Раздел «Опционы» (вилки ставка НЕТ + опцион) — тоже только для своих.
const OptionsGate: React.FC = () => {
  const user = useAppStore(s => s.user);
  if (!user) return null;
  return canSeeOptions(user) ? <OptionsPage /> : <Navigate to="/scanner" replace />;
};
const AdminPage         = lazy(() => import('./pages/Admin'));
// Админка — только админам (utils/adminAccess); сервер сам отвечает 403 остальным.
const AdminGate: React.FC = () => {
  const user = useAppStore(s => s.user);
  if (!user) return null;
  return isAdmin(user) ? <AdminPage /> : <Navigate to="/" replace />;
};
const PortfolioPage     = lazy(() => import('./pages/Portfolio'));
const FarmsPage         = lazy(() => import('./pages/Farms'));
const SpreadChartsPage  = lazy(() => import('./pages/SpreadCharts'));
const AutoArbitrage = lazy(() => import('./pages/AutoArbitrage'));
const AutoSessionNew = lazy(withPageNs('autoSessionNew', () => import('./pages/AutoSessionNew')));
const VenuePresetsPage  = lazy(withPageNs('venuePresets', () => import('./pages/VenuePresets')));
const SnakeGame         = lazy(() => import('./pages/SnakeGame'));
const ChessGame         = lazy(() => import('./pages/ChessGame'));
const CheckersGame      = lazy(() => import('./pages/CheckersGame'));
const MinesweeperGame   = lazy(() => import('./pages/MinesweeperGame'));
const HelpPage          = lazy(withPageNs('helpContent', () => import('./pages/Help')));
// Мост LayerZero OFT (/bridge), пункт «Бридж» в меню «Ещё».
const BridgePage        = lazy(() => import('./pages/Bridge'));
const LeaderboardPage   = lazy(() => import('./pages/Leaderboard'));
const CalendarPage      = lazy(() => import('./pages/Calendar'));

const Protected: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAuth = useAppStore((s) => s.isAuthenticated);
  const loc = useLocation();
  if (!isAuth) return <Navigate to="/login" replace state={{ from: loc.pathname + loc.search }} />;
  return (
    <AppShell>
      <PageErrorBoundary>
        {/* Заглушка на время догрузки чанка страницы. Общая с остальными
            состояниями загрузки, чтобы переход не выглядел поломкой. */}
        <Suspense fallback={<LoadingState className="py-24" />}>{children}</Suspense>
      </PageErrorBoundary>
    </AppShell>
  );
};

/** Обёртки: страницам нужно знать, кто я — от этого зависят «мой ход» в
 *  партии, «мои сообщения» в чате и список друзей. */
const GamesRoute: React.FC = () => {
  const me = useAppStore(s => s.user?.username) || '';
  return <GamesPage me={me} />;
};

const ChatRoute: React.FC = () => {
  const me = useAppStore(s => s.user?.username) || '';
  return <ChatPage me={me} />;
};

const App: React.FC = () => {
  const { isAuthenticated, loadUserSettings } = useAppStore();

  useEffect(() => {
    const init = async () => {
      if (!isAuthenticated) return;
      try {
        await loadUserSettings();
        void syncServerPrefs();
        if (!wsManager.isConnected()) await wsManager.connect();
      } catch (err) {
        console.error('init failed:', err);
      }
    };
    init();
  }, [isAuthenticated, loadUserSettings]);

  return (
    <>
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />

          {/* Main — Futures Terminal (Лонг+Шорт) */}
          <Route path="/"           element={<Protected><Terminal /></Protected>} />
          <Route path="/scanner"    element={<Protected><DashboardV3 /></Protected>} />
          {/* Вкладка Routes удалена по решению владельца: её работу целиком закрыл
              Авто-Арбитраж. Адрес оставлен редиректом — закладки не должны падать. */}
          <Route path="/routes"     element={<Navigate to="/auto" replace />} />
          <Route path="/routes/:id" element={<Protected><RouteDetail /></Protected>} />
          <Route path="/api-keys"   element={<Protected><ApiKeysPage /></Protected>} />
          <Route path="/alerts"     element={<Protected><AlertsPage /></Protected>} />
          {/* Пресеты связок: правило «биржи + порог» вместо выбора монеты */}
          <Route path="/funding"    element={<Protected><FundingPage /></Protected>} />
          {/* Токенизированные акции xStocks: сети, биржи, связки через мост */}
          <Route path="/xstocks"    element={<Protected><XStocksGate /></Protected>} />
          {/* Вилки Polymarket/Kalshi «НЕТ» + опцион Deribit/Bybit/Binance/OKX */}
          <Route path="/options"    element={<Protected><OptionsGate /></Protected>} />
          {/* Админка: люди, сессии автозалива, биржи, журнал (только админам) */}
          <Route path="/admin"      element={<Protected><AdminGate /></Protected>} />
          {/* Сетка независимых графиков спреда (в т.ч. кросс-символьных) */}
          <Route path="/charts"     element={<Protected><SpreadChartsPage /></Protected>} />
          {/* Страница PnL убрана 26.09 — итоги сделок живут в портфеле. */}
          <Route path="/pnl"        element={<Navigate to="/portfolio" replace />} />
          <Route path="/portfolio"  element={<Protected><PortfolioPage /></Protected>} />
          <Route path="/farms"      element={<Protected><FarmsPage /></Protected>} />
          {/* Балансы по биржам — своя страница из меню, с опросом по кнопке */}
          <Route path="/balances"   element={<Protected><BalancesPage /></Protected>} />
          <Route path="/help"       element={<Protected><HelpPage /></Protected>} />
          <Route path="/bridge"     element={<Protected><BridgePage /></Protected>} />
          <Route path="/leaderboard" element={<Protected><LeaderboardPage /></Protected>} />
          <Route path="/calendar"   element={<Protected><CalendarPage /></Protected>} />
          {/* Служебная диагностика, в меню намеренно нет — см. FeedHealth.tsx */}
          <Route path="/feed-health" element={<Protected><FeedHealthPage /></Protected>} />
          {/* Игры и чат — свои страницы. Раньше и то, и другое жило внутри
              профиля, и «открыть профиль» с «открыть игры» вели в одно место. */}
          <Route path="/games"      element={<Protected><GamesRoute /></Protected>} />
          <Route path="/chat"       element={<Protected><ChatRoute /></Protected>} />
          <Route path="/profile"    element={<Protected><ProfilePage /></Protected>} />
          {/* Вкладка профиля «Безопасность»: вход, устройства, сессии. */}
          <Route path="/security"   element={<Protected><SecurityPage /></Protected>} />
          <Route path="/achievements" element={<Protected><AchievementsPage /></Protected>} />
          {/* Профиль с именем в адресе: свой открывается как обычно, чужой —
              в режиме просмотра. Ссылку такого вида можно кинуть человеку. */}
          <Route path="/profile/:username" element={<Protected><ProfilePage /></Protected>} />
          {/* legacy redirect — все ссылки уже переведены на /profile */}
          <Route path="/settings"   element={<Navigate to="/profile" replace />} />

          {/* Авто-Арбитраж — сессии автоматических связок. Старый путь /infinity
              оставлен живым: он в закладках и в старых ссылках. */}
          <Route path="/auto"       element={<Protected><AutoArbitrage /></Protected>} />
          <Route path="/auto/new"   element={<Protected><AutoSessionNew /></Protected>} />
          {/* Пресеты — часть раздела «Авто-Арбитраж», а не свой пункт меню:
              пресет это та же сессия, заведённая правилом. Старый адрес
              оставлен редиректом — закладки и ссылки не должны падать. */}
          <Route path="/auto/presets" element={<Protected><VenuePresetsPage /></Protected>} />
          <Route path="/presets"    element={<Navigate to="/auto/presets" replace />} />
          <Route path="/infinity"   element={<Navigate to="/auto" replace />} />
          {/* Snake / Chess easter-eggs — открываются из меню профиля. */}
          <Route path="/snake"      element={<Protected><SnakeGame /></Protected>} />
          <Route path="/chess"      element={<Protected><ChessGame /></Protected>} />
          <Route path="/checkers"   element={<Protected><CheckersGame /></Protected>} />
          <Route path="/minesweeper" element={<Protected><MinesweeperGame /></Protected>} />

          {/* Заглушки для ещё не готовых режимов */}
          {/* Отдельной страницы «Спот + Шорт» больше нет: спот-площадки стали
              обычными биржами в общей матрице и живут в сканере, funding-скринере
              и терминале. Старую ссылку не ломаем — уводим в сканер. */}
          <Route path="/spot"       element={<Navigate to="/scanner" replace />} />

          {/* Legacy-редирект, пока не вычистили все внешние ссылки */}
          <Route path="/dashboard"  element={<Navigate to="/" replace />} />
          <Route path="*"           element={<Navigate to="/" replace />} />
        </Routes>
      </Router>

      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: 'hsl(var(--card))',
            color: 'hsl(var(--card-foreground))',
            border: '1px solid hsl(var(--border))',
          },
          success: { iconTheme: { primary: '#10b981', secondary: '#fff' } },
          error:   { iconTheme: { primary: '#ef4444', secondary: '#fff' } },
        }}
      >
        {/* Уведомление перекрывало кнопки в правом верхнем углу, а по
            умолчанию наведение его ещё и замораживало. Теперь навёл — оно
            уходит, и пока уезжает, клики проходят сквозь него. */}
        {(t) => (
          <div
            onMouseEnter={() => toast.dismiss(t.id)}
            style={{ pointerEvents: t.visible ? 'auto' : 'none' }}
          >
            <ToastBar toast={t} />
          </div>
        )}
      </Toaster>
    </>
  );
};

export default App;
